import { useEffect, useState, type ReactNode } from 'react';
import { CheckCircle2, Circle, ChevronDown, ListChecks, FileText, XCircle } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

/* ────────────────────────────────────────────────────────────
   Key takeaways — answers the query above the fold
   ──────────────────────────────────────────────────────────── */

export function KeyTakeaways({
  items,
  title = 'What you need to know',
  titleKo = '핵심 요약',
}: {
  items: ReactNode[];
  title?: string;
  titleKo?: string;
}) {
  return (
    <section className="my-8 overflow-hidden rounded-2xl border border-ink-200 bg-white shadow-sm dark:border-surface-dark-border dark:bg-surface-dark-card">
      {/* Full Obangsaek stripe — this is the article's signature block */}
      <div className="flex h-1 w-full" aria-hidden="true">
        <div className="flex-1 bg-[#C8102E]" />
        <div className="flex-1 bg-[#0047AB]" />
        <div className="flex-1 bg-[#FFB81C]" />
        <div className="flex-1 bg-[#F5F5F0]" />
        <div className="flex-1 bg-[#1C1C1C]" />
      </div>

      <div className="p-5 sm:p-6">
        <div className="mb-4 flex items-baseline gap-2">
          <h2 className="text-lg font-bold text-ink-900 dark:text-ink-50">{title}</h2>
          <span lang="ko" className="font-korean text-sm text-ink-500 dark:text-ink-400">
            {titleKo}
          </span>
        </div>

        <ul className="space-y-3">
          {items.map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <CheckCircle2
                className="mt-0.5 h-4 w-4 flex-shrink-0 text-jeok-500 dark:text-jeok-dark"
                aria-hidden="true"
              />
              <span className="text-[15px] leading-7 text-ink-700 dark:text-ink-200">{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────
   Table of contents — scroll-spy, sticky on desktop
   ──────────────────────────────────────────────────────────── */

export interface TocItem {
  id: string;
  label: string;
  labelKo?: string;
}

export function TableOfContents({ items, className }: { items: TocItem[]; className?: string }) {
  const [activeId, setActiveId] = useState<string>(items[0]?.id ?? '');

  useEffect(() => {
    const headings = items
      .map((i) => document.getElementById(i.id))
      .filter((el): el is HTMLElement => el !== null);
    if (headings.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      // Top band of the viewport, below the 64px sticky header.
      { rootMargin: '-80px 0px -70% 0px', threshold: 0 }
    );

    headings.forEach((h) => observer.observe(h));
    return () => observer.disconnect();
  }, [items]);

  return (
    <nav
      aria-label="Table of contents"
      className={twMerge(
        'rounded-xl border border-ink-200 bg-white p-4 dark:border-surface-dark-border dark:bg-surface-dark-card',
        className
      )}
    >
      <p className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-400">
        <ListChecks className="h-3.5 w-3.5" aria-hidden="true" />
        In this guide
      </p>
      <ol className="space-y-1">
        {items.map((item, i) => (
          <li key={item.id}>
            <a
              href={`#${item.id}`}
              aria-current={activeId === item.id ? 'true' : undefined}
              className={twMerge(
                'flex gap-2 rounded-lg px-2 py-1.5 text-sm leading-snug transition-colors',
                activeId === item.id
                  ? 'bg-jeok-50 font-semibold text-jeok-500 dark:bg-jeok-500/10 dark:text-jeok-dark'
                  : 'text-ink-500 hover:bg-ink-50 hover:text-ink-900 dark:text-ink-400 dark:hover:bg-white/5 dark:hover:text-ink-50'
              )}
            >
              <span className="font-mono text-xs tabular-nums opacity-60">
                {String(i + 1).padStart(2, '0')}
              </span>
              <span className="min-w-0">{item.label}</span>
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

/* ────────────────────────────────────────────────────────────
   Step card — numbered, scannable process steps
   ──────────────────────────────────────────────────────────── */

export function StepCard({
  step,
  title,
  titleKo,
  meta,
  children,
}: {
  step: number;
  title: string;
  titleKo?: string;
  /** Right-aligned metadata: cost, processing time, where it's done. */
  meta?: { label: string; value: string }[];
  children: ReactNode;
}) {
  return (
    <div className="relative rounded-xl border border-ink-200 bg-white p-5 shadow-sm transition-shadow duration-200 hover:shadow-md dark:border-surface-dark-border dark:bg-surface-dark-card">
      <div className="flex items-start gap-4">
        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-jeok-500 font-mono text-sm font-bold tabular-nums text-white dark:bg-jeok-dark">
          {step}
        </span>

        <div className="min-w-0 flex-1">
          <h3 className="text-base font-bold leading-snug text-ink-900 dark:text-ink-50">{title}</h3>
          {titleKo && (
            <p lang="ko" className="mt-0.5 font-korean text-sm text-ink-500 dark:text-ink-400">
              {titleKo}
            </p>
          )}

          <div className="mt-2 text-[15px] leading-7 text-ink-700 dark:text-ink-200 [&>p:last-child]:mb-0 [&>p]:mb-3">
            {children}
          </div>

          {meta && meta.length > 0 && (
            <dl className="mt-4 flex flex-wrap gap-x-6 gap-y-2 border-t border-ink-100 pt-3 dark:border-surface-dark-border">
              {meta.map((m) => (
                <div key={m.label}>
                  <dt className="text-[11px] uppercase tracking-wide text-ink-400">{m.label}</dt>
                  <dd className="text-sm font-semibold text-ink-900 dark:text-ink-50">{m.value}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      </div>
    </div>
  );
}

export function StepList({ children }: { children: ReactNode }) {
  return <div className="my-8 space-y-4">{children}</div>;
}

/* ────────────────────────────────────────────────────────────
   Document checklist — interactive, persists per guide
   ──────────────────────────────────────────────────────────── */

export interface ChecklistItem {
  id: string;
  label: string;
  labelKo?: string;
  /** Where the document comes from / how to get it. */
  detail?: string;
  /** Renders a "required" vs "if applicable" chip. */
  optional?: boolean;
}

export function DocumentChecklist({
  storageKey,
  title,
  titleKo,
  items,
}: {
  /** localStorage key so progress survives a reload mid-application. */
  storageKey: string;
  title: string;
  titleKo?: string;
  items: ChecklistItem[];
}) {
  const [checked, setChecked] = useState<Set<string>>(new Set());

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw) setChecked(new Set(JSON.parse(raw) as string[]));
    } catch {
      // Private mode / quota — the checklist still works, it just won't persist.
    }
  }, [storageKey]);

  const toggle = (id: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      try {
        window.localStorage.setItem(storageKey, JSON.stringify([...next]));
      } catch {
        /* non-fatal */
      }
      return next;
    });
  };

  const done = items.filter((i) => checked.has(i.id)).length;
  const pct = Math.round((done / items.length) * 100);

  return (
    <section className="my-10 overflow-hidden rounded-2xl border border-ink-200 bg-white shadow-sm dark:border-surface-dark-border dark:bg-surface-dark-card">
      <div className="border-b border-ink-200 bg-ink-50 p-5 dark:border-surface-dark-border dark:bg-white/[0.03]">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="flex items-center gap-2 text-base font-bold text-ink-900 dark:text-ink-50">
              <FileText className="h-4 w-4 text-jeok-500 dark:text-jeok-dark" aria-hidden="true" />
              {title}
            </h3>
            {titleKo && (
              <p lang="ko" className="mt-0.5 font-korean text-sm text-ink-500 dark:text-ink-400">
                {titleKo}
              </p>
            )}
          </div>
          <span className="flex-shrink-0 font-mono text-sm font-semibold tabular-nums text-ink-500 dark:text-ink-400">
            {done}/{items.length}
          </span>
        </div>

        <div
          className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-ink-200 dark:bg-white/10"
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Documents gathered"
        >
          <div
            className="h-full rounded-full bg-jeok-500 transition-[width] duration-300 ease-out dark:bg-jeok-dark"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      <ul className="divide-y divide-ink-100 dark:divide-surface-dark-border">
        {items.map((item) => {
          const isDone = checked.has(item.id);
          return (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => toggle(item.id)}
                aria-pressed={isDone}
                className="flex w-full items-start gap-3 p-4 text-left transition-colors hover:bg-ink-50 dark:hover:bg-white/[0.03]"
              >
                {isDone ? (
                  <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600 dark:text-green-500" aria-hidden="true" />
                ) : (
                  <Circle className="mt-0.5 h-5 w-5 flex-shrink-0 text-ink-200 dark:text-ink-500" aria-hidden="true" />
                )}

                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <span
                      className={twMerge(
                        'text-sm font-medium text-ink-900 dark:text-ink-50',
                        isDone && 'text-ink-400 line-through dark:text-ink-500'
                      )}
                    >
                      {item.label}
                    </span>
                    {item.optional && (
                      <span className="rounded-full bg-ink-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-ink-500 dark:bg-white/10 dark:text-ink-400">
                        If applicable
                      </span>
                    )}
                  </span>
                  {item.labelKo && (
                    <span lang="ko" className="mt-0.5 block font-korean text-xs text-ink-500 dark:text-ink-400">
                      {item.labelKo}
                    </span>
                  )}
                  {item.detail && (
                    <span className="mt-1 block text-xs leading-relaxed text-ink-500 dark:text-ink-400">
                      {item.detail}
                    </span>
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────
   Eligibility cards — qualify / don't qualify, side by side
   ──────────────────────────────────────────────────────────── */

export function EligibilityGrid({ children }: { children: ReactNode }) {
  return <div className="my-8 grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>;
}

export function EligibilityCard({
  variant,
  title,
  titleKo,
  items,
}: {
  variant: 'qualify' | 'disqualify';
  title: string;
  titleKo?: string;
  items: string[];
}) {
  const isQualify = variant === 'qualify';
  const Icon = isQualify ? CheckCircle2 : XCircle;

  return (
    <div
      className={twMerge(
        'rounded-xl border p-5 shadow-sm',
        isQualify
          ? 'border-green-200 bg-green-50/60 dark:border-green-900/40 dark:bg-green-900/10'
          : 'border-jeok-200 bg-jeok-50/60 dark:border-jeok-dark/30 dark:bg-jeok-500/[0.07]'
      )}
    >
      <h3
        className={twMerge(
          'flex items-center gap-2 text-sm font-bold',
          isQualify ? 'text-green-800 dark:text-green-400' : 'text-jeok-500 dark:text-jeok-dark'
        )}
      >
        <Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
        {title}
      </h3>
      {titleKo && (
        <p lang="ko" className="mt-0.5 font-korean text-xs text-ink-500 dark:text-ink-400">
          {titleKo}
        </p>
      )}

      <ul className="mt-3 space-y-2">
        {items.map((item) => (
          <li
            key={item}
            className="flex items-start gap-2 text-sm leading-6 text-ink-700 dark:text-ink-200"
          >
            <span
              className={twMerge(
                'mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full',
                isQualify ? 'bg-green-600' : 'bg-jeok-500 dark:bg-jeok-dark'
              )}
            />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   FAQ — renders the same Q&A that feeds FAQPage structured data
   ──────────────────────────────────────────────────────────── */

export interface FaqItem {
  question: string;
  /** Plain text — it is also serialised into JSON-LD, so keep it markup-free. */
  answer: string;
}

export function FAQ({ items, id = 'faq' }: { items: FaqItem[]; id?: string }) {
  return (
    <section id={id} className="my-10 scroll-mt-24">
      <h2 className="mb-1 text-2xl font-bold text-ink-900 dark:text-ink-50">
        Frequently asked questions
      </h2>
      <p lang="ko" className="mb-5 font-korean text-base text-ink-500 dark:text-ink-400">
        자주 묻는 질문
      </p>

      <div className="space-y-3">
        {items.map((item) => (
          <details
            key={item.question}
            className="group overflow-hidden rounded-xl border border-ink-200 bg-white shadow-sm dark:border-surface-dark-border dark:bg-surface-dark-card"
          >
            <summary className="flex cursor-pointer list-none items-start justify-between gap-4 p-4 text-[15px] font-semibold text-ink-900 transition-colors hover:bg-ink-50 dark:text-ink-50 dark:hover:bg-white/[0.03]">
              {item.question}
              <ChevronDown
                className="mt-0.5 h-4 w-4 flex-shrink-0 text-ink-400 transition-transform duration-200 group-open:rotate-180"
                aria-hidden="true"
              />
            </summary>
            <div className="border-t border-ink-100 px-4 py-4 text-[15px] leading-7 text-ink-700 dark:border-surface-dark-border dark:text-ink-200">
              {item.answer}
            </div>
          </details>
        ))}
      </div>
    </section>
  );
}
